import { onScopeDispose, ref, type Ref } from 'vue'

import { dynamicDropdownsApi, type DynamicDropdownsApi } from '../api/DynamicDropdownsApi'
import type {
  DynamicDropdownMap,
  DynamicDropdownQuery,
  DynamicDropdownScope,
} from '../models/dynamicDropdowns.types'
import { buildDynamicDropdownQuery, dynamicDropdownUrl } from '../service/dynamicDropdownRequest'

export type DynamicDropdownState = 'ready' | 'loading' | 'error'

export type DynamicDropdownAnswers = readonly (DynamicDropdownMap | null)[]

export interface UseDynamicDropdowns {
  state: Ref<DynamicDropdownState>
  load: (
    scope: DynamicDropdownScope,
    queries: readonly DynamicDropdownQuery[],
  ) => Promise<DynamicDropdownAnswers | null>
  cancel: () => void
}

export function useDynamicDropdowns(
  api: Pick<DynamicDropdownsApi, 'fetchDropdowns'> = dynamicDropdownsApi,
): UseDynamicDropdowns {
  const state = ref<DynamicDropdownState>('ready')
  const served = new Map<string, DynamicDropdownMap>()
  let request: AbortController | null = null

  function cancel(): void {
    request?.abort()
    request = null
  }

  async function fetchOne(
    url: string,
    query: string,
    signal: AbortSignal,
  ): Promise<DynamicDropdownMap | null> {
    const key = `${url}?${query}`
    const cached = served.get(key)
    if (cached) return cached
    try {
      const map = await api.fetchDropdowns(url, query, signal)
      served.set(key, map)
      return map
    } catch {
      return null
    }
  }

  async function load(
    scope: DynamicDropdownScope,
    queries: readonly DynamicDropdownQuery[],
  ): Promise<DynamicDropdownAnswers | null> {
    request?.abort()
    const controller = new AbortController()
    request = controller

    const url = dynamicDropdownUrl(scope)
    const built = queries.map((query) => buildDynamicDropdownQuery(query))
    state.value = built.every((query) => served.has(`${url}?${query}`)) ? 'ready' : 'loading'

    const answers = await Promise.all(built.map((query) => fetchOne(url, query, controller.signal)))
    if (controller !== request) return null

    request = null
    state.value = answers.length > 0 && answers.every((map) => map === null) ? 'error' : 'ready'
    return answers
  }

  onScopeDispose(cancel)
  return { state, load, cancel }
}
